import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { Agent } from "model/agent";
import { Avatar } from "component/shared/avatar";
import { Tag } from "component/shared/tag";
import { useTheme } from "@mui/material/styles";
import { PhotoModal } from "./photo-modal";
import { getAgentBoardsForAgent, getBoardsForUser, getLanguagesByIds, getAgentMlsIds, getUser } from "redux/selector";
import { profileSplit } from "./profile-split";
import { mlsSetupRefresh } from "shared/env";
import { RootState } from "redux/store";
import { getMemberRoleLabel } from "./member-meta";

interface Props {
	agent: Agent;
	/** Account has no team context — drops Member ID, roles, languages and MLS IDs. */
	isSingleAgent?: boolean;
	/** Rendered as the member's own Profile page; scopes the plan-tier gating to that page. */
	isSelfView?: boolean;
	// classes are drilled from the parent ProfileRefresh sheet (this is not a withStyles-wrapped component)
	classes?: Record<string, string>;
}

const SummaryField: React.FC<{ classes: any; label: string }> = ({ classes, label, children }) => (
	<div className={classes.summaryField}>
		<Mui.Typography className={classes.summaryLabel}>{label}</Mui.Typography>
		<Mui.Typography className={classes.summaryValue}>{children}</Mui.Typography>
	</div>
);

/**
 * Read-only member summary (left panel) for the refreshed detail view. No inline editing —
 * contact/login editing now lives in the Profile and Access tabs.
 */
export const Summary: React.FC<Props> = ({ agent, isSingleAgent = false, isSelfView = false, classes = {} }) => {
	const theme = useTheme();
	const user = useSelector(getUser);
	// Self-view only. The member-detail view is already live in production via refreshTeam, and its
	// Team section admits any broker admin -- gating unconditionally would strip these fields from
	// every agent a non-CRM broker manages (MAX-4139).
	const showTeamFields = !isSelfView
		|| !!(user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures);
	const agentBoards = useSelector((state: RootState) => getAgentBoardsForAgent(state, agent.id));
	const boards = useSelector(getBoardsForUser);
	const languages = useSelector((state: RootState) => getLanguagesByIds(state, agent.languageIds));

	const agentMlsIds = useSelector(getAgentMlsIds);

	const boardName = (boardId: number) => boards.find((b) => b.id === boardId)?.shortName;
	const agentBoardMlsEntries = () => agentBoards
		.filter((ab) => ab.agentCode)
		.map((ab) => {
			const name = boardName(ab.boardId);
			return name ? `${ab.agentCode} (${name})` : ab.agentCode;
		});
	// With the MLS Setup redesign on (and the account able to read them): the same agent MLS IDs as the Profile tab's, so a modal save shows here too.
	const agentMlsIdEntries = () => {
		if (!agentMlsIds) {
			return [];
		}
		const boardNames = new Map(agentMlsIds.boards.map((board) => [board.boardId, board.name]));
		return profileSplit(agentMlsIds, agent.id).flatMap(({ boardId, ids, strays }) =>
			[...ids, ...strays].map((entry) => {
				const name = boardNames.get(boardId);
				return name ? `${entry.mlsId} (${name})` : entry.mlsId;
			})
		);
	};
	const mlsEntries = mlsSetupRefresh && user.permissions.officeAccountType === true ? agentMlsIdEntries() : agentBoardMlsEntries();

	const roleLabel = getMemberRoleLabel(agent);
	const languageNames = languages.map((l) => l.name).join(", ");
	const bioUrl = agent.hasOwnSite ? (agent.redirectUrl || agent.agentPageUrl) : agent.agentPageUrl;
	const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

	return (
		<div>
			<div className={classes.avatarBlock}>
				<div className={classes.avatarWrap}>
					<Avatar phrase={agent.label} src={agent.photoUrl} diameter={112} color={theme.palette.primary.main} />
					<Mui.IconButton
						className={classes.editPhotoBadge}
						size="small"
						aria-label="Edit photo"
						onClick={() => setPhotoDialogOpen(true)}
					>
						<Icons.Edit2 size={12} />
					</Mui.IconButton>
				</div>
				<Mui.Typography className={classes.memberName}>{agent.name}</Mui.Typography>
				{agent.isAccountOwner && <Tag label="Account Owner" variant="warning" size="small" />}
				{bioUrl && (
					<Mui.Link href={bioUrl} target="_blank" rel="noopener" className={classes.viewBioLink}>
						View Bio Page
						<Icons.ExternalLink size={14} />
					</Mui.Link>
				)}
			</div>

			<Mui.Divider className={classes.summaryDivider} />

			<SummaryField classes={classes} label="Title / Designation">{agent.designation || "–"}</SummaryField>
			{/* A single-agent account has no team to be a member of, so the identifiers and roles
			    that only make sense against a roster drop out (MAX-4139). MLS IDs and Languages
			    additionally require the team plan tier. */}
			{!isSingleAgent && <SummaryField classes={classes} label="Member ID">{agent.id}</SummaryField>}
			{!isSingleAgent && showTeamFields && (
				<SummaryField classes={classes} label="MLS ID">
					{mlsEntries.length ? mlsEntries.map((e, i) => <div key={i}>{e}</div>) : "–"}
				</SummaryField>
			)}
			{!isSingleAgent && <SummaryField classes={classes} label="Member Roles">{roleLabel || "Member"}</SummaryField>}
			<SummaryField classes={classes} label="Office">{agent.office?.name || "–"}</SummaryField>
			{!isSingleAgent && showTeamFields && (
				<SummaryField classes={classes} label="Languages">{languageNames || "–"}</SummaryField>
			)}

			<Mui.Divider className={classes.summaryDivider} />

			<SummaryField classes={classes} label="Email">{agent.email || "–"}</SummaryField>
			<SummaryField classes={classes} label="Cell Phone">{agent.cellPhone || "–"}</SummaryField>
			<SummaryField classes={classes} label="Office Phone">{agent.officePhone || "–"}</SummaryField>

			<PhotoModal open={photoDialogOpen} agent={agent} onClose={() => setPhotoDialogOpen(false)} />
		</div>
	);
};
